/**
 * Deal types. Wire shapes are generated from the Rust DTOs in
 * `src/transports/api/deals.rs`. Deal state + product-type unions are
 * hand-written SPA-side narrowings over the generated `string` fields
 * (Rust serializes those via `&'static str` rather than a typed enum).
 */

import type { CatalogProductType } from "./generated/CatalogProductType";
import type { CatalogProductName } from "./generated/CatalogProductName";
import type { DealLineageResponse as DealLineageResponseDto } from "./generated/DealLineageResponse";
import type { DealListResponse as DealListResponseDto } from "./generated/DealListResponse";
import type { DealOverviewDto } from "./generated/DealOverviewDto";
import type { DealRelationshipDto } from "./generated/DealRelationshipDto";
import type { DealRelationshipsResponse as DealRelationshipsResponseDto } from "./generated/DealRelationshipsResponse";
import type { DealSummaryDto } from "./generated/DealSummaryDto";
import type { LineageNodeDto } from "./generated/LineageNodeDto";

export type { CatalogProductType } from "./generated/CatalogProductType";
export type { CatalogProductName } from "./generated/CatalogProductName";
export type { DealListKpisDto as DealListKpis } from "./generated/DealListKpisDto";
export type { DealOwnerOptionDto as DealOwnerOption } from "./generated/DealOwnerOptionDto";
export type { DealOfferingOptionDto as DealOfferingOption } from "./generated/DealOfferingOptionDto";
export type { DealFilterFacetsDto as DealFilterFacets } from "./generated/DealFilterFacetsDto";
export type { DealClientOptionDto as DealClientOption } from "./generated/DealClientOptionDto";
export type { DealType } from "./generated/DealType";
export type { DealPickerOptionDto as DealPickerOption } from "./generated/DealPickerOptionDto";
export type { PipelineDto as Pipeline } from "./generated/PipelineDto";
export type { AssignDealCustomerRequest as AssignDealCustomerInput } from "./generated/AssignDealCustomerRequest";
export type { DealPricingAssumptionsDto as DealPricingAssumptions } from "./generated/DealPricingAssumptionsDto";

// ── Deal state ────────────────────────────────────────────────────────────────

/**
 * Deal lifecycle state. Mirrors the Rust `DealStateDiscriminant` enum
 * (snake_case wire code). Terminal states: `closed_won`, `closed_lost`.
 *
 * Kept in lock-step with `app::deals::discriminant_code` on the server.
 */
export type DealState =
  | "qualified"
  | "discovery"
  | "bid_requested"
  | "proposed"
  | "awaiting_signature"
  | "closed_won"
  | "closed_lost";

export const DEAL_STATE_LABELS: Record<DealState, string> = {
  qualified: "Qualified",
  discovery: "Discovery",
  bid_requested: "Bid Requested",
  proposed: "Proposed",
  awaiting_signature: "Awaiting Signature",
  closed_won: "Closed Won",
  closed_lost: "Closed Lost",
};

/** Display order — used by the pipeline stepper and filter dropdown. */
export const DEAL_STATES_ORDERED: DealState[] = [
  "qualified",
  "discovery",
  "bid_requested",
  "proposed",
  "awaiting_signature",
  "closed_won",
  "closed_lost",
];

export function isTerminalState(s: DealState): boolean {
  return s === "closed_won" || s === "closed_lost";
}

// ── Product-type groupings (gate conditional form fields) ─────────────────────

/**
 * Three product-type groups drive conditional field visibility in the New
 * Deal sheet. Mirrors the legacy JS data-new-group attribute:
 *   · Group A — energy commodity: Term + Volume + Margin visible
 *   · Group B — advisory / audit:  Term + Gross Revenue visible
 *   · Group C — everything else:   Gross Revenue only visible
 */
export type ProductTypeGroup = "a" | "b" | "c";

export function productTypeGroup(type: CatalogProductType): ProductTypeGroup {
  switch (type) {
    case "electric":
    case "natural_gas":
    case "carbon_offsets":
    case "recs":
    case "utility_bill_payment":
      return "a";
    case "bill_audit":
    case "demand_response":
      return "b";
    default:
      return "c";
  }
}

export const CATALOG_PRODUCT_TYPE_LABELS: Record<CatalogProductType, string> = {
  electric: "Electric",
  natural_gas: "Natural Gas",
  carbon_offsets: "Carbon Offsets",
  recs: "RECs",
  utility_bill_payment: "Utility Bill Payment",
  demand_response: "Demand Response",
  budget_development: "Budget Development",
  bill_audit: "Bill Audit",
  water_solution: "Water Solution",
  battery_storage: "Battery Storage",
  ppa_vppa: "PPA/VPPA",
  solar: "Solar",
  energy_efficiency: "Energy Efficiency",
  utility_rebate: "Utility Rebate",
  license: "License",
};

// ── Product name (variant within a commodity product type) ───────────────────

/**
 * Product-name labels. Mirrors the Rust `CatalogProductName::display_label`.
 * Product name is a sub-variant that applies only to commodity types
 * (electric / natural_gas); the DB enforces it's null for everything else.
 */
export const CATALOG_PRODUCT_NAME_LABELS: Record<CatalogProductName, string> = {
  fixed: "Fixed",
  index: "Index",
  block_volume: "Block Hedge",
  block_percentage: "Load Following Hedge",
};

/** All selectable product names, in display order. */
export const CATALOG_PRODUCT_NAMES: ReadonlyArray<CatalogProductName> = [
  "fixed",
  "index",
  "block_volume",
  "block_percentage",
];

/**
 * Whether a product type carries a product-name variant. True only for the
 * commodities (electric / natural_gas) — matches the server's catalog
 * constraint and gates the "Product name" field in the New Deal wizard.
 */
export function productTypeHasName(
  type: CatalogProductType | "" | null | undefined,
): boolean {
  return type === "electric" || type === "natural_gas";
}

// ── Narrowed response shapes ─────────────────────────────────────────────────

/**
 * Deal list row. Narrows `DealSummaryDto.state` (generated as `string` from
 * the Rust `&'static str`) to the typed `DealState` union.
 */
export type DealSummary = Omit<DealSummaryDto, "state" | "lineage_relation"> & {
  state: DealState;
  lineage_relation: LineageRelation | null;
};

/**
 * Paginated response. Narrows the generated DTO's `items` array to the
 * typed `DealSummary[]` above.
 */
export type DealListResponse = Omit<DealListResponseDto, "items"> & {
  items: DealSummary[];
};

/**
 * Customer's response to the proposal. Snake-case codes match the
 * Rust enum (`ProposalResponse`) — kept as a union here because the
 * enum doesn't derive `Serialize`, so the API maps it to a string code.
 */
export type ProposalResponse = "pending" | "accepted" | "rejected" | "amended";

/**
 * Detail-page overview. Narrows the generated `state` string to the typed
 * `DealState` union — same pattern as `DealSummary`. Also narrows
 * `proposal_response` from a loose `string` (server emits `&'static str`)
 * to the typed `ProposalResponse` union.
 */
export type DealOverview = Omit<
  DealOverviewDto,
  "state" | "proposal_response" | "lineage_relation"
> & {
  state: DealState;
  proposal_response: ProposalResponse | null;
  lineage_relation: LineageRelation | null;
};

// ── Lineage (renewal / follow-on chain) ──────────────────────────────────────

/**
 * How a deal in a lineage chain was created from its predecessor.
 * `origin` = first in the chain; `renewal` = renewed from the previous
 * (Closed Won); `follow_on` = follow-on of the previous (Closed Lost).
 * Mirrors `LineageRelation::code` on the server.
 */
export type LineageRelation = "origin" | "renewal" | "follow_on";

/**
 * One node in a deal's lineage chain. Narrows the generated `state`
 * (`string`) and `relation` (`string`) to typed unions.
 */
export type LineageNode = Omit<LineageNodeDto, "state" | "relation"> & {
  state: DealState;
  relation: LineageRelation;
};

/** Lineage response — chain ordered oldest-first. Narrows `nodes`. */
export type DealLineage = Omit<DealLineageResponseDto, "nodes"> & {
  nodes: LineageNode[];
};

// ── Mutation payload ─────────────────────────────────────────────────────────

export interface CreateDealInput {
  customer_id: string;
  /** Free-form description. Optional — deals can be saved without one;
   *  the UI falls back to `Deal #<number>` for unset descriptions. */
  description?: string | null;
  pipeline_id: string;
  product_type: CatalogProductType;
  /** Product variant (Fixed / Index / Block) for MEP/Retail direct-close
   *  commodity deals. Null for non-commodity products and RFQ-flow deals. */
  product_name?: CatalogProductName | null;
  deal_type?: import("./generated/DealType").DealType | null;
  assigned_to?: string | null;
  agent_id?: string | null;
  segment_id?: string | null;
  /** ISO `yyyy-MM-dd`. */
  close_date?: string | null;
  /** ISO `yyyy-MM-dd`. */
  start_date?: string | null;
  /** ISO `yyyy-MM-dd`. Bidirectional with `term_months` on MEP/Retail/CCA. */
  end_date?: string | null;
  price_value?: string | null;
  price_uom?: string | null;
  block_volume?: string | null;
  block_volume_uom?: string | null;
  block_percentage?: string | null;
  product_description?: string | null;
  state_region?: string | null;
  /** AB#8154 — scopes which subdivisions `state_region` may hold. */
  country?: string | null;
  /** Stringified Decimal — preserved precision across the wire. */
  amount?: string | null;
  gross_revenue?: string | null;
  annual_volume?: string | null;
  term_months?: number | null;
  margin_value?: string | null;
  margin_uom?: string | null;
  /** CCA participation percent (AB#7087); null = no scaling. */
  participation_percentage?: string | null;
  /** Supplier serving a CCA direct-close deal (AB#7087). */
  supplier_tenant_id?: string | null;
  /** Parent deal id — set when creating a renewal from an existing
   *  closed-won deal. The server stores it on `deals.renewed_from_deal_id`.
   *  Null for net-new deals. */
  renewed_from_deal_id?: string | null;
  /** Parent deal id for non-renewal spawns (Blend & Extend, Block
   *  Purchase). The server stores it on `deals.parent_deal_id`.
   *  Distinct from `renewed_from_deal_id`. Null on net-new. */
  parent_deal_id?: string | null;
}

/**
 * `PUT /api/deals/:id` body. Shape mirrors `CreateDealInput` minus
 * `customer_id` (not editable — legacy behavior) and with
 * `product_type` optional (can be cleared on edit).
 */
export interface UpdateDealInput {
  /** Free-form description. Optional — sending null/empty clears it. */
  description?: string | null;
  pipeline_id: string;
  product_type?: CatalogProductType | null;
  /** Product variant (Fixed / Index / Block) for MEP/Retail direct-close
   *  commodity deals. Null for non-commodity products and RFQ-flow deals. */
  product_name?: CatalogProductName | null;
  deal_type?: import("./generated/DealType").DealType | null;
  assigned_to?: string | null;
  agent_id?: string | null;
  segment_id?: string | null;
  close_date?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  price_value?: string | null;
  price_uom?: string | null;
  block_volume?: string | null;
  block_volume_uom?: string | null;
  block_percentage?: string | null;
  product_description?: string | null;
  state_region?: string | null;
  /** AB#8154 — scopes which subdivisions `state_region` may hold. */
  country?: string | null;
  amount?: string | null;
  gross_revenue?: string | null;
  annual_volume?: string | null;
  term_months?: number | null;
  margin_value?: string | null;
  margin_uom?: string | null;
  /** CCA participation percent (AB#7087); null clears the stored value. */
  participation_percentage?: string | null;
  /** Supplier serving a CCA direct-close deal (AB#7087); null clears it. */
  supplier_tenant_id?: string | null;
}

/**
 * One deal relationship. Narrows the loose `other_deal_state: string` from the
 * wire to the typed `DealState` union — same `Omit + &` pattern used elsewhere.
 */
export type DealRelationship = Omit<DealRelationshipDto, "other_deal_state"> & {
  other_deal_state: DealState;
};

/** Relationships list + the picker candidates, in one response. */
export type DealRelationshipsResponse = Omit<
  DealRelationshipsResponseDto,
  "items"
> & {
  items: DealRelationship[];
};

/** The canonical relationship type codes the API accepts. */
export type RelationshipType =
  | "renewal"
  | "blend_extend"
  | "block_index"
  | "assignment"
  | "associated";

/** Body for `POST /api/deals/:deal_id/relationships`. */
export interface CreateRelationshipInput {
  other_deal_id: string;
  relationship_type: RelationshipType;
  direction: "outbound" | "inbound";
  /** ISO yyyy-MM-dd; only sent for assignment. */
  assignment_date?: string | null;
  /** Optional free-form label (mainly for `associated`). */
  label?: string | null;
}

// ── Deal-account dropped fields ──────────────────────────────────────────────

/**
 * Why an account was dropped from service on a deal. Mirrors the
 * `dropped_reason` CHECK on `deals.deal_accounts` and the handler's
 * `DROPPED_REASONS` guard (the column is text on the server, so the typed
 * union lives here).
 */
export type DroppedReason =
  | "account_closed"
  | "assignment"
  | "changed_suppliers"
  | "relocation"
  | "other";

export const DROPPED_REASON_LABELS: Record<DroppedReason, string> = {
  account_closed: "Account Closed",
  assignment: "Assignment",
  changed_suppliers: "Changed Suppliers",
  relocation: "Relocation",
  other: "Other",
};

/** Drop reasons in display order, for the edit sheet's select. */
export const DROPPED_REASON_OPTIONS: { value: DroppedReason; label: string }[] =
  (Object.keys(DROPPED_REASON_LABELS) as DroppedReason[]).map((value) => ({
    value,
    label: DROPPED_REASON_LABELS[value],
  }));

/**
 * Body for `PUT /api/deals/:deal_id/pricing-assumptions`. Not generated —
 * mirrors the Rust `UpdateDealPricingAssumptionsRequest` (AB#7559).
 */
export interface UpdateDealPricingAssumptionsRequest {
  /** AB#8264: the deal's PLC tag history. The server derives the single
   *  `plc_kw` the capacity math reads from this list, so the request no
   *  longer carries it. */
  plc_tags: PlcTagInput[];
  capacity_usd_per_mw_day: string | null;
  henry_hub_settle: string | null;
  heat_content: string | null;
  hedge_ratio_pct: string | null;
  /** "fwd" (current forward) | "avg" (2-yr rolling avg). */
  unhedged_basis: "fwd" | "avg" | null;
  two_year_avg_usd_per_mwh: string | null;
  /** "fwd" (latest settle) | "avg" (2-yr rolling avg). */
  nymex_basis: "fwd" | "avg" | null;
  /** Reference wholesale price for the pass-through estimate, $/MWh. */
  wholesale_energy_price_usd_per_mwh: string | null;
  /** Load-following hedge price for the pass-through estimate, $/MWh. */
  hedge_price_usd_per_mwh: string | null;
  hedge_structure: HedgeStructure | null;
  delivery_voltage: DeliveryVoltage | null;
  ancillary_stat: AncillaryStat | null;
  length_treatment: LengthTreatment | null;
  /** Price every contract month at `wholesale_energy_price_usd_per_mwh`
   *  instead of at the forward curve. Requires that price to be set. */
  pin_energy_price: boolean;
  /** AB#8308: the settlement zone the advisor picked. Null derives it.
   *  Required: the endpoint replaces the whole set, so a caller that leaves
   *  it out clears the deal's zone. */
  pricing_zone_id: string | null;
  /** Contract length in months. Required for the same reason. */
  term_months: number | null;
  /** AB#8918: the gas pass-through rates the estimate falls back to when a
   *  supplier states none, canonical `$/therm`. Required for the same
   *  reason: a caller that leaves them out clears them. */
  gas_basis_usd_per_therm: string | null;
  gas_transportation_usd_per_therm: string | null;
  gas_storage_usd_per_therm: string | null;
  gas_balancing_usd_per_therm: string | null;
}

/** One capacity (PLC) tag as the editor submits it (AB#8264). A tag runs
 *  mid-year to mid-year, so its range normally spans two consecutive years. */
export interface PlcTagInput {
  /** kW to two decimal places, sent as a string so the value the desk typed
   *  reaches the server unrounded (AB#8264). */
  plc_kw: string;
  tag_start_year: number;
  tag_end_year: number;
}

/** How a deal's energy is hedged — drives the estimate's energy leg. */
export type HedgeStructure =
  | "load_following"
  | "block"
  | "block_plus_load_following"
  | "index";

/** Which loss row the estimate grosses up on. */
export type DeliveryVoltage = "avg" | "transmission" | "primary" | "secondary";

/** Which ancillary row the estimate prices against. */
export type AncillaryStat = "avg" | "min" | "max";

/** What to do with hours where a block exceeds load. */
export type LengthTreatment = "net_at_index" | "no_credit";

/** One month of a deal's load shape, as a fraction of annual volume. */
export type { DealLoadShapeDto as DealLoadShape } from "./generated/DealLoadShapeDto";
export type { ResolvedLoadShapeDto as ResolvedLoadShape } from "./generated/ResolvedLoadShapeDto";

/** Which rung of the ladder supplied the deal's seasonality. Mirrors the
 *  Rust `MonthlyShapeSource::code()`; the DTO types it as a loose string. */
export type MonthlyShapeSource =
  | "stated"
  | "measured"
  | "archetype"
  | "balanced";

/** One block-hedge cell: month, product, flat MW and price. */
export type { DealHedgeBlockDto as DealHedgeBlock } from "./generated/DealHedgeBlockDto";

/** The four block products, in the order the editor lists them. */
export const BLOCK_PRODUCTS = [
  "peak",
  "two_by_16",
  "seven_by_8",
  "atc",
] as const;
export type BlockProduct = (typeof BLOCK_PRODUCTS)[number];

/** Body for `PUT /api/deals/:deal_id/load-shape`. */
export type UpdateDealLoadShapeRequest = {
  month_num: number;
  shape_pct: string;
}[];

/** Body for `PUT /api/deals/:deal_id/hedge-blocks`. */
export type UpdateDealHedgeBlocksRequest = {
  month_num: number;
  product: BlockProduct;
  mw: string;
  price: string;
}[];

/** Body for the full-row deal-account update (`PUT /api/deals/:id/accounts/:id`). */
export interface UpdateDealAccountInput {
  /** ISO `yyyy-MM-dd`. Empty leaves the start unset. */
  start_date?: string;
  /** Ignore `start_date` and reset to the deal's own start_date. */
  reset_to_default?: boolean;
  /** ISO `yyyy-MM-dd` manual end-date override. Empty = follow the deal's end. */
  end_date?: string;
  dropped?: boolean;
  dropped_reason?: DroppedReason | "";
  /** ISO `yyyy-MM-dd`. */
  dropped_date?: string;
}
